/* ═══════════════════════════════════════════════════════════════════════════
   CÓPIA. O original vive em mypedidos-menu-frontend/src/utils/color.js, e é lá
   que a regra se decide — aqui ela só é espelhada.

   Existe uma cópia porque os dois repositórios são separados e não há pacote
   compartilhado entre eles, e existe uma SÓ regra porque o preview desta tela
   precisa mostrar exatamente a cor que o cardápio vai exibir. Um preview com
   matemática própria mente para o lojista sobre contraste, e mentir sobre
   contraste é pior do que não ter preview: ele aprova uma cor achando que está
   legível.

   Ao mudar aqui, mude lá — e ao mudar lá, copie para cá. O `color.test.js` ao
   lado guarda os mesmos vetores dos dois lados, incluindo a `direcao` do ajuste:
   é ela que decide QUAL frase o lojista lê, e uma tabela só de valores pegaria
   um tom errado sem pegar um aviso apontando para o lado errado.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════
   Derivação da paleta de marca a partir de UMA cor.

   O lojista escolhe um hex no admin. A interface usa cinco tons: o cheio
   (botão, preço, chip ativo), o claro do gradiente, o fantasma de fundo e dois
   escuros para as superfícies sólidas de navegação. Pedir cinco cores ao
   lojista seria pedir que ele fosse designer; pedir uma e derivar o resto é o
   que mantém o cardápio coerente qualquer que seja a escolha.

   As proporções abaixo não são arbitrárias: aplicadas ao roxo da plataforma
   (#534AB7) elas reproduzem os cinco tons que já estavam no `variables.css`
   escritos à mão. O cheio sai idêntico; fantasma, escuro e profundo erram um
   ponto por canal; o claro erra três. Ou seja — estabelecimento sem cor
   configurada continua vendo o cardápio de antes.

   Os números acima são medidos, não estimados: se alguém mexer nas proporções
   e o desvio crescer, é regressão. O teste guarda os cinco valores.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Contraste mínimo do WCAG para texto normal. O tom cheio carrega texto
 *  branco em 42 lugares do CSS (botão de ação, cabeçalho, chip ativo): abaixo
 *  disso o rótulo do botão principal deixa de ser legível. */
const CONTRASTE_MINIMO = 4.5;

/** Abaixo disto o estado do botão inverte de direção, em vez de a cor ser
 *  clareada.
 *
 *  O escuro e o profundo são o hover e o pressionado do botão de ação, e saem
 *  como frações do cheio (0,77 e 0,58). Isso funciona enquanto há de onde
 *  descer: com o cheio em 0,50 eles caem em 0,39 e 0,29, três degraus que o
 *  dedo percebe. Com o cheio quase preto eles caem em 0,03 e 0,02, os três
 *  viram o mesmo preto, e o botão para de responder ao toque — o `scale(0.99)`
 *  ainda acontece, mas a cor não muda mais.
 *
 *  A saída não é clarear a cor do lojista. Preto é escolha legítima de marca, e
 *  devolver um azul-escuro no lugar do preto que ele pediu é o mesmo tipo de
 *  surpresa que a derivação existe para evitar. A saída é inverter o sentido do
 *  estado: numa paleta escura, o pressionado vai para o CLARO. É o que qualquer
 *  interface de tema escuro faz com elevação, porque abaixo do preto não há
 *  para onde ir.
 *
 *  Assim a cor escolhida chega intacta ao cardápio, `direcao` continua 'nenhum'
 *  — nada foi ajustado — e o botão volta a ter retorno visual.
 *
 *  0,22 é onde o degrau multiplicativo fica pequeno demais para ser visto. Cor
 *  escura legítima com folga continua descendo: o #0B7A4B do WhatsApp em 0,26 e
 *  o roxo profundo da plataforma em 0,29 não são afetados. */
const LIMIAR_ESTADO_INVERTIDO = 0.22;

/** Degraus do hover e do pressionado quando o estado sobe em vez de descer.
 *  Absolutos, e não proporcionais: proporcional ao que é quase zero continua
 *  quase zero, que é justamente o problema. */
const PASSO_HOVER_CLARO = 0.09;
const PASSO_ATIVO_CLARO = 0.16;

const HEX_VALIDO = /^#[0-9a-fA-F]{6}$/;

export function parseHex(hex) {
  if (typeof hex !== 'string' || !HEX_VALIDO.test(hex.trim())) return null;
  const n = parseInt(hex.trim().slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function paraHex({ r, g, b }) {
  const oito = (v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${oito(r)}${oito(g)}${oito(b)}`.toUpperCase();
}

function rgbParaHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslParaRgb({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let rgb;
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

/** Luminância relativa do WCAG — não é a mesma coisa que o L do HSL. O L trata
 *  amarelo e azul da mesma forma; o olho não, e é o olho que decide se o texto
 *  do botão dá para ler. */
function luminancia({ r, g, b }) {
  const canal = (v) => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

export function contraste(a, b) {
  const [claro, escuro] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (claro + 0.05) / (escuro + 0.05);
}

export function contrasteComBranco(rgb) {
  return contraste(rgb, { r: 255, g: 255, b: 255 });
}

/** Escurece mantendo matiz e saturação até o texto por cima passar no WCAG.
 *  A cor exibida pode não ser a escolhida — é de propósito: um amarelo puro
 *  precisaria virar mostarda para que o botão continue legível, e um botão
 *  ilegível é pior falha de marca do que um tom deslocado.
 *
 *  O piso é o tom fantasma, não o branco. São dois papéis em direções opostas
 *  e o cheio precisa servir aos dois: ele é PREENCHIMENTO sob texto branco
 *  (botão de ação, cabeçalho) e é TEXTO sobre o fantasma (chip, hover da ação
 *  secundária, aviso do Pix). O fantasma é um pouco mais escuro que o branco,
 *  então quem passa nele passa nos dois — clampar só contra o branco deixaria
 *  o texto do chip reprovando por uma fração.
 *
 *  Devolve também se foi preciso mexer, para o chamador poder avisar. */
function ajustarLuminosidade(hsl, fundoMaisClaro) {
  // A medição é feita na cor JÁ arredondada para hex, e não no HSL contínuo:
  // é o hex que vai para o navegador, e a fração perdida no arredondamento é
  // suficiente para um tom sair em 4,49 depois de ter passado em 4,50.
  const passa = (l) =>
    contraste(parseHex(paraHex(hslParaRgb({ ...hsl, l }))), fundoMaisClaro) >= CONTRASTE_MINIMO;

  let l = hsl.l;

  // Escurece até o texto por cima passar no WCAG. É o único ajuste que a cor
  // sofre: não há piso de luminosidade, e cor escura chega ao cardápio como o
  // lojista escolheu. O que muda para as muito escuras é o SENTIDO do hover e
  // do pressionado, mais abaixo, não a cor.
  while (l > 0.02 && !passa(l)) l -= 0.01;

  // A DIREÇÃO vai junto, e não só o fato de ter mexido.
  //
  // Enquanto só existia o piso de contraste, "ajustado" queria dizer uma coisa
  // só — escurecemos — e quem consome podia assumir isso. Com o teto, ele passou
  // a cobrir dois movimentos opostos, e um consumidor que continuasse assumindo
  // o antigo diria ao lojista que a cor foi escurecida quando ela foi clareada.
  // Direção errada é pior que nenhum aviso: manda procurar no lugar errado o que
  // corrigir, e o motivo também é outro — clarear não é legibilidade, é o
  // cabeçalho deixar de se distinguir do botão.
  //
  // Quem sabe a direção é aqui. Deixar o consumidor deduzi-la comparando
  // contraste é obrigá-lo a redescobrir o que esta função acabou de decidir.
  const delta = l - hsl.l;
  const direcao = delta > 0.001 ? 'clareou' : delta < -0.001 ? 'escureceu' : 'nenhum';

  return { hsl: { ...hsl, l }, direcao };
}

/**
 * Recebe o hex do estabelecimento, devolve os cinco tons + o anel de foco.
 * `null` para cor ausente ou malformada — o chamador cai no roxo dos tokens.
 */
export function derivarMarca(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return null;

  const original = rgbParaHsl(rgb);

  // Cinza puro não tem matiz para derivar: os cinco tons sairiam todos iguais
  // e o cardápio ficaria sem cor de ação nenhuma.
  if (original.s < 0.05) return null;

  const { h } = original;

  // O fantasma sai primeiro porque é ele o piso de contraste do tom cheio: só
  // depende do matiz, e é o fundo mais claro sobre o qual o cheio vira texto.
  const ghostHsl = { h, s: 0.9, l: 0.96 };
  const tom = (props) => paraHex(hslParaRgb({ h, ...props }));
  const ghostHex = tom(ghostHsl);

  const { hsl: base, direcao } = ajustarLuminosidade(original, parseHex(ghostHex));
  const { s } = base;

  return {
    // Cheio: botão, preço, chip ativo. Branco por cima dele, ele por cima do
    // fantasma — passa nos dois.
    primary: tom({ s, l: base.l }),
    // Claro do gradiente e estados de hover.
    //
    // Não passa pelo clamp de 4,5:1, e isso é deliberado: nos oito lugares em
    // que o CSS o usa ele é `border-color`, `fill` ou `stroke` — nunca fundo
    // sob texto. O mínimo que vale para ele é o de elemento não textual, 3:1.
    // Escurecê-lo até 4,5:1 só apagaria a borda clara que ele existe para
    // fazer. Se um dia o claro virar fundo de texto, aí sim ele entra no
    // clamp — mas aí a mudança é essa, e não "consertar um esquecimento".
    mid: tom({ s: Math.min(1, s * 1.35), l: Math.min(0.92, base.l + 0.165) }),
    // Fundo de destaque, com o tom cheio escrito por cima.
    ghost: ghostHex,

    // Hover e pressionado do botão de ação, e as superfícies sólidas de
    // navegação. Descem a partir do cheio — exceto quando o cheio já está tão
    // escuro que descer não produz degrau visível, e aí sobem. Ver o comentário
    // do LIMIAR_ESTADO_INVERTIDO: abaixo do preto não há para onde ir, e um
    // botão que não muda de cor ao ser tocado parece travado.
    ...(base.l < LIMIAR_ESTADO_INVERTIDO
      ? {
        dark: tom({ s, l: base.l + PASSO_HOVER_CLARO }),
        deep: tom({ s, l: base.l + PASSO_ATIVO_CLARO }),
      }
      : {
        dark: tom({ s, l: base.l * 0.77 }),
        deep: tom({ s, l: base.l * 0.58 }),
      }),
    focus: (() => {
      const { r, g, b } = hslParaRgb({ h, s, l: base.l });
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.35)`;
    })(),

    // 'nenhum' | 'escureceu' | 'clareou'. Quem só precisa saber SE mexemos usa
    // `ajustado`; quem vai escrever a frase que o lojista lê precisa da direção,
    // porque os dois movimentos têm motivos diferentes e apontam para correções
    // diferentes.
    direcao,
    ajustado: direcao !== 'nenhum',
  };
}
